import * as THREE from 'three';
import { initMagnetGun, updateMagnetGun } from './magnet.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { config, initGUI } from './config.js';
import { loadAudioFiles } from './audio.js';
import { setupLights, createStarfield, setupFloor, updateEnvironment } from './environment.js';
import { loadRapier, setupPhysics, physicsWorld } from './physics.js';
import { stepPhysics } from './physics.js';
import { createCylinders } from './objects-cylinders.js';
import { loadAndCreateSVGObjects, updateSVGEmissions } from './objects-svg.js';
import { loadHelmetModel } from './objects-models.js';
import { updateRigidBodies } from './objects-shared.js';
import { createAllPages, updateHTMLPageVideos } from './objects-page.js';
import { loadAllDynamicPages } from './objects-dpage.js';
import { loadAndCreateImageObjects, updateImageEmissions } from './objects-images.js';
import { createPlayer, setupControls, updatePlayer, updateProjectiles } from './player.js';
import { initDebugRenderer, updateDebugRenderer } from './debug.js';
import { deltaTime } from 'three/tsl';

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 5000);
const renderer = new THREE.WebGLRenderer({ 
    antialias: false,  // Wyłącz - bloom i tak rozmywa
    powerPreference: "high-performance",
    stencil: false,  // Nie używasz stencil buffer
    depth: true
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); 

renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.2;
document.body.appendChild(renderer.domElement);

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    config.bloom.strength,
    config.bloom.radius,
    config.bloom.threshold
);
composer.addPass(bloom);

const clock = new THREE.Clock();
let fpsCounter = 0;
let lastFpsTime = 0;

function createFPSCounter() {
    const fpsDiv = document.createElement('div');
    fpsDiv.id = 'fps-counter';
    fpsDiv.textContent = 'FPS: 0';
    document.body.appendChild(fpsDiv);
    return fpsDiv;
}

const fpsDisplay = createFPSCounter();
const physicsSteps = stepPhysics(deltaTime);
function animate() {
    requestAnimationFrame(animate);
    const dt = Math.min(clock.getDelta(), 0.016);
    const time = clock.getElapsedTime();

    fpsCounter++;
    if (time - lastFpsTime >= 1) {
        fpsDisplay.textContent = `FPS: ${fpsCounter}`;
        fpsCounter = 0;
        lastFpsTime = time;
    }

    if (!physicsWorld) return;

    updateEnvironment(time);
    updatePlayer(dt, camera);
    updateMagnetGun(dt);  
    
    physicsWorld.step();
    
    updateRigidBodies();
    updateSVGEmissions();
    updateImageEmissions();
    updateHTMLPageVideos();
    updateProjectiles(scene);
    updateDebugRenderer();

    bloom.strength = config.bloom.strength;
    bloom.radius = config.bloom.radius;
    bloom.threshold = config.bloom.threshold;

    composer.render();
}

let loadProgress = 0;
const totalSteps = 9;

function updateLoader(step, stepProgress = 1) {
    loadProgress = ((step - 1) + stepProgress) / totalSteps;
    const percentage = Math.floor(loadProgress * 100);
    document.getElementById('loader-percent').textContent = percentage + '%';
    document.getElementById('bar').style.width = percentage + '%';
}

async function init() {
    updateLoader(1, 0.5);
    await loadRapier();
    updateLoader(1, 1);
    
    updateLoader(2, 0.5);
    await loadAudioFiles();
    updateLoader(2, 1);
    
    updateLoader(3, 0.3);
    setupLights(scene);
    updateLoader(3, 0.6);
    createStarfield(scene);
    updateLoader(3, 1);
    
    updateLoader(4, 0.5);
    setupFloor(scene);
    setupPhysics();
    updateLoader(4, 1);
    
    updateLoader(5, 0.5);
    createPlayer();
    initMagnetGun(scene);
    initDebugRenderer(scene);
    createCylinders(scene);
    updateLoader(5, 1);
    
    updateLoader(6, 0.3);
    await loadAndCreateSVGObjects(scene);
    updateLoader(6, 1);
    
    updateLoader(7, 0.5);
    await loadHelmetModel(scene);
    updateLoader(7, 1);
    
    updateLoader(8, 0.3);
    await loadAndCreateImageObjects(scene);
    updateLoader(8, 1);
    
    updateLoader(9, 0.3);
    await createAllPages(scene);
    await loadAllDynamicPages(scene);
    updateLoader(9, 0.6);
    setupControls(renderer);
    initGUI();
    updateLoader(9, 1);

    gsap.to(document.getElementById('loader'), { 
        opacity: 0, 
        pointerEvents: 'none', 
        duration: 0.9, 
        delay: 0.3 
    });

    window.addEventListener('resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
        composer.setSize(window.innerWidth, window.innerHeight);
        bloom.setSize(window.innerWidth, window.innerHeight);
    });

    animate();
}

init();